// src/api/hr.ts
import { api } from './client';
import {
  ID, Paginated,
  LeaveRequest, LeaveFilters, CreateLeaveDTO, UpdateLeaveDTO,
  TravelRequest, TravelFilters, CreateTravelDTO, UpdateTravelDTO,
  ExpenseAdvance, ExpenseAdvanceFilters, CreateExpenseAdvanceDTO, UpdateExpenseAdvanceDTO,
  ExpenseLiquidation, CreateExpenseLiquidationDTO, UpdateExpenseLiquidationDTO,
  LoanRequest, LoanFilters, CreateLoanDTO, UpdateLoanDTO,
  LeaveBalance, HolidayCalendar,
} from '../types/hr';

/* ===========================
   Types for Approvals (modern)
   =========================== */
export type ApprovalStatusLite =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'SKIPPED'
  | 'CANCELLED'
  | 'ANY';

export interface ApprovalListItem {
  id: number; // TaskApproval.id (or adapt if your API returns taskId)
  taskId: number;
  status: ApprovalStatusLite;
  actedAt?: string | null;
  comment?: string | null;

  // step + task context (server includes these via include)
  stepDef?: any;
  task?: {
    id: number;
    title?: string;
    description?: string;
    type?: string;
    approvalStatus?: string | null;
    currentApprovalStep?: number | null;
    createdBy?: {
      id: number;
      username?: string | null;
      profile?: { imageUrl?: string | null } | null;
    } | null;
    project?: {
      id: number;
      name?: string | null;
      department?: { id: number; name?: string | null; workspaceId: number };
    } | null;
  } | null;
}

export interface ApprovalListResponse {
  total: number;
  page: number;
  limit: number;
  items: ApprovalListItem[];
}

/* ===========================
   Types for HR Approvals (legacy facade)
   =========================== */
export interface HRApprovalRow {
  id: ID; // taskId (approval id)
  entity: string; // e.g. 'LEAVE_REQUEST'
  title: string;
  submittedBy: {
    id?: ID;
    username?: string;
    profile?: any; // generic to match your backend Profile
  };
  submittedAt: string; // ISO
  href: string; // deep link to entity page
  step?: number; // current step order (optional)
}

/* ===========================
   Leaves
   =========================== */
export const hrLeaves = {
  list: async (workspaceId: ID, q: LeaveFilters = {}) =>
    (await api.get<Paginated<LeaveRequest>>('/hr/leaves', {
      params: { workspaceId, ...q },
    })).data,

  get: async (id: ID) =>
    (await api.get<LeaveRequest>(`/hr/leaves/${id}`)).data,

  create: async (payload: CreateLeaveDTO) =>
    (await api.post<LeaveRequest>('/hr/leaves', payload)).data,

  update: async (id: ID, payload: UpdateLeaveDTO) =>
    (await api.patch<LeaveRequest>(`/hr/leaves/${id}`, payload)).data,

  softDelete: async (id: ID) =>
    (await api.post(`/hr/leaves/${id}/soft-delete`)).data,
};

/* ===========================
   Travels
   =========================== */
export const hrTravels = {
  list: async (workspaceId: ID, q: TravelFilters = {}) =>
    (await api.get<Paginated<TravelRequest>>('/hr/travels', {
      params: { workspaceId, ...q },
    })).data,

  get: async (id: ID) =>
    (await api.get<TravelRequest>(`/hr/travels/${id}`)).data,

  create: async (payload: CreateTravelDTO) =>
    (await api.post<TravelRequest>('/hr/travels', payload)).data,

  update: async (id: ID, payload: UpdateTravelDTO) =>
    (await api.patch<TravelRequest>(`/hr/travels/${id}`, payload)).data,

  softDelete: async (id: ID) =>
    (await api.post(`/hr/travels/${id}/soft-delete`)).data,
};

/* ===========================
   Expense Advances
   =========================== */
// Narrow the create payload so TS won’t suggest sending createdById/userId
type CreateAdvancePayload = Omit<CreateExpenseAdvanceDTO, 'createdById' | 'userId'>;

export const hrAdvances = {
  list: async (workspaceId: ID, q: ExpenseAdvanceFilters = {}) =>
    (await api.get<Paginated<ExpenseAdvance>>('/hr/expense-advances', {
      params: { workspaceId, ...q },
    })).data,

  get: async (id: ID) =>
    (await api.get<ExpenseAdvance>(`/hr/expense-advances/${id}`)).data,

  create: async (payload: CreateAdvancePayload) =>
    (await api.post<ExpenseAdvance>('/hr/expense-advances', payload)).data,

  update: async (id: ID, payload: UpdateExpenseAdvanceDTO) =>
    (await api.patch<ExpenseAdvance>(`/hr/expense-advances/${id}`, payload)).data,

  softDelete: async (id: ID) =>
    (await api.post(`/hr/expense-advances/${id}/soft-delete`)).data,

  // Liquidations
  createLiquidation: async (payload: CreateExpenseLiquidationDTO) =>
    (await api.post<ExpenseLiquidation>('/hr/expense-liquidations', payload)).data,

  updateLiquidation: async (id: ID, payload: UpdateExpenseLiquidationDTO) =>
    (await api.patch<ExpenseLiquidation>(`/hr/expense-liquidations/${id}`, payload)).data,

  deleteLiquidation: async (id: ID) =>
    (await api.delete(`/hr/expense-liquidations/${id}`)).data,
};

/* ===========================
   Loans
   =========================== */
export const hrLoans = {
  list: async (workspaceId: ID, q: LoanFilters = {}) =>
    (await api.get<Paginated<LoanRequest>>('/hr/loans', {
      params: { workspaceId, ...q },
    })).data,

  get: async (id: ID) =>
    (await api.get<LoanRequest>(`/hr/loans/${id}`)).data,

  create: async (payload: CreateLoanDTO) =>
    (await api.post<LoanRequest>('/hr/loans', payload)).data,

  update: async (id: ID, payload: UpdateLoanDTO) =>
    (await api.patch<LoanRequest>(`/hr/loans/${id}`, payload)).data,

  softDelete: async (id: ID) =>
    (await api.post(`/hr/loans/${id}/soft-delete`)).data,
};

/* ===========================
   Admin: Leave Balances
   =========================== */
export const hrBalances = {
  get: async (workspaceId: ID, userId: ID, year: number) =>
    (await api.get<LeaveBalance>('/hr/leave-balance', {
      params: { workspaceId, userId, year },
    })).data,

  upsert: async (payload: LeaveBalance) =>
    (await api.post<LeaveBalance>('/hr/leave-balance', payload)).data,
};

/* ===========================
   Admin: Holiday Calendars
   =========================== */
export const hrHolidays = {
  list: async (workspaceId: ID) =>
    (await api.get<HolidayCalendar[]>('/hr/holiday-calendars', {
      params: { workspaceId },
    })).data,

  create: async (payload: Omit<HolidayCalendar, 'id'>) =>
    (await api.post<HolidayCalendar>('/hr/holiday-calendars', payload)).data,

  update: async (id: ID, payload: Partial<HolidayCalendar>) =>
    (await api.patch<HolidayCalendar>(`/hr/holiday-calendars/${id}`, payload)).data,

  remove: async (id: ID) =>
    (await api.delete(`/hr/holiday-calendars/${id}`)).data,
};

/* ===========================
   Approvals (modern endpoints)
   =========================== */
export const approvalsApi = {
  /**
   * Fetch the current user’s approvals.
   * By default we return only the current step and only PENDING items.
   */
  listMy: async (opts?: {
    status?: ApprovalStatusLite;       // default 'PENDING'
    taskType?: string;
    currentStepOnly?: boolean;         // default true
    page?: number;
    limit?: number;
    q?: string;
  }) => {
    const params = {
      status: opts?.status ?? 'PENDING',
      taskType: opts?.taskType,
      currentStepOnly:
        typeof opts?.currentStepOnly === 'boolean' ? opts?.currentStepOnly : true,
      page: opts?.page,
      limit: opts?.limit,
      q: opts?.q,
    };
    return (await api.get<ApprovalListResponse>('/approvals/my', { params })).data;
  },

  approve: async (taskId: ID, note?: string) =>
    (await api.post(`/approvals/${taskId}/approve`, { note })).data,

  reject: async (taskId: ID, note?: string) =>
    (await api.post(`/approvals/${taskId}/reject`, { note })).data,
};

/* ===========================
   Approvals (legacy HR facade)
   =========================== */
export const hrApprovals = {
  // Backend derives the user from JWT; no params needed.
  list: async () =>
    (await api.get<HRApprovalRow[]>('/hr/approvals')).data,

  act: async (approvalId: ID, action: 'APPROVE' | 'REJECT', note?: string) =>
    (await api.post(`/hr/approvals/${approvalId}/act`, { action, note })).data,
};
